import cn from 'clsx'
import Pane from 'components/Common/Pane'
import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import OfferPageGallery from '../OfferPageGallery'
import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { clearBookingCalendar, fetchCalendars } from 'actions/CalendarActions'
import {
  fetchOfferFlightPrice,
  fetchRelatedTravelItems,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Subtitle from 'LuxKit/Typography/Subtitle'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { replace } from 'connected-react-router'
import { urlHashDatePicker } from 'constants/url'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import usePropertyRates from 'hooks/usePropertyRates'
import { generateRoomOccupants, isValidOccupancy } from 'lib/offer/occupancyUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import offerPageURL from 'lib/offer/offerPageURL'
import { encodeSearchParams } from 'lib/search/searchUtils'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDatePickerSelect from '../Filters/DatePicker/OfferDatePickerSelect'
import OfferDatePickerSelectBox from '../Filters/DatePicker/OfferDatePickerSelectBox'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import AlwaysOnSidebarFilters from '../AlwaysOn/AlwaysOnSidebarFilters'
import ModalProvider from 'contexts/ModalProvider'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import useShareModalConfig from 'hooks/useShareModalConfig'
import PropertyRoomOptions from './PropertyRoomOptions'
import PropertyIntro from './PropertyIntro'
import PropertyStickyHeader from './PropertyStickyHeader'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { getCheckoutDate, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { useLocation } from 'react-router'
import useOffer from 'hooks/Offers/useOffer'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import PropertyRoomOptionsMobile from './MobileBookingFlow/PropertyRoomOptionsMobile'
import AccommodationOfferPageStickyHeaderMobile from '../Accommodation/AccommodationOfferPageStickyHeaderMobile'
import AlwaysOnMobileStickyHeaderFilters from '../AlwaysOn/MobileBookingFlow/AlwaysOnMobileStickyHeaderFilters'
import PropertyStickyFooter from './PropertyStickyFooter'
import AccommodationOfferPageStickyFooter from '../Accommodation/AccommodationOfferPageStickyFooter'
import { useShowBusinessTravellerSelectionModal } from 'businessTraveller/hooks/useShowBusinessTravellerSelectionModal'
import { BusinessTravellerMultiRoomHotelsOfferPageDesktop, BusinessTravellerMultiRoomHotelsOfferPageDesktopDisabled } from 'businessTraveller/components/select-traveller/multi-room-hotels'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import BusinessTravellerSelectLoadingSkeleton from 'businessTraveller/components/select-traveller/BusinessTravellerSelectLoadingSkeleton'

const RelativeBlock = styled.form`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.no-padding {
    padding: 0;
  }
`

interface Props {
  offer: App.HotelOffer;
  airport?: App.Airport;
  duration: number;
  activeFilter?: OfferPageFilterType;
  activeHeaderFilter?: OfferPageFilterType;
  rooms?: Array<App.RoomOccupants>;
  checkInDate?: string;
  checkOutDate?: string;
  availableForPurchase?: boolean;
  room?: App.PackageRoomType;
  regionCode: string;
  utmContent: string | undefined;
  selectedTravellerEmployeeIds?: Array<string>;
  selectedTravellerGuestCount?: number;
  currentBusinessAccount?: App.BusinessTraveller.CurrentBusinessAccount;
}

const fallbackRooms = [generateRoomOccupants()]

const currentTimer = {} as Record<string, any>

const slideTimer = (name: string, callback: any, ms: number) => {
  if (currentTimer[name]) {
    clearTimeout(currentTimer[name])
  }
  currentTimer[name] = setTimeout(callback, ms)
}

function PropertyContent(props: Props) {
  const {
    offer,
    airport,
    duration,
    activeFilter,
    rooms = fallbackRooms,
    checkInDate,
    checkOutDate,
    availableForPurchase,
    activeHeaderFilter,
    regionCode,
    utmContent,
    selectedTravellerEmployeeIds,
    selectedTravellerGuestCount,
    currentBusinessAccount,
  } = props

  const isLebtMultiRoomHotelEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtMultiRoomHotel)

  const location = useLocation()
  const query = useQsQueryParams<{ roomRateId?: string, roomTypeId?: string }>()

  const [viewedDates, setViewedDates] = useState(!!checkInDate)

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()

  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants>>([])
  const lastFetchedAirport = useRef<App.Airport | null>(null)
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)

  const isMobileDevice = useScreenSizeOnly('mobile')
  const isNewMobileBookingFlowEnabled = useOptimizelyExperiment(OptimizelyExperiments.croAlwaysOnNewMobileBooking, isMobileDevice)
  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowEnabled

  useShowBusinessTravellerSelectionModal({
    selectedTravellerEmployeeIds,
    selectedTravellerGuestCount,
    currentBusinessAccount,
    routerLocationSearch: location.search,
    stateDispatch,
  })

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
    }).toString(), [checkInDate, checkOutDate, rooms, location.search])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  useIdleTimeReporter(IdleTimeSource.AlwaysOn)

  const fetchCalendarData = useCallback(() => {
    if (!offer.packages.length || !isValidOccupancy(...rooms)) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }

    dispatch(fetchCalendars(offer.id, {
      occupants: rooms,
      flightOrigin: airport?.code,
      packages: [offer.lowestPricePackage!],
    }))
    dispatch(fetchCalendars(offer.id, {
      occupants: rooms,
      flightOrigin: airport?.code,
      packages: offer.packages.filter(p => p.duration === duration),
    }))

    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport ?? null
  }, [airport, dispatch, duration, offer, rooms])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const hasRoomsChanged = useMemo(() => {
    const currentRooms = rooms ?? fallbackRooms
    const currentOccupancyNumber = currentRooms.map(generateOccupancyStringByRoom).join('|')
    const lastOccupancyNumber = (lastFetchedRooms.current ?? fallbackRooms).map(generateOccupancyStringByRoom).join('|')
    return currentOccupancyNumber !== lastOccupancyNumber
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, activeFilter])

  useEffect(() => {
    const hasRooms = rooms !== fallbackRooms
    if (previousFilter === 'capacity' && !hasRooms) {
      if (!activeFilter) {
        autoScrollToFilters()
        scrollAfterFilterClose.current = false
      }
    }

    if ((activeFilter === 'checkin' || activeHeaderFilter === 'checkin') && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else if (viewedDates && lastFetchedAirport.current !== airport) {
      // Filter has just closed, so we're applying it now
      // rooms has changed, all loaded calendars are no longer valid as we load calendars for specific
      // rooms (availablity), so clear them and refetch
      dispatch(clearBookingCalendar())
      fetchCalendarData()
    } else if (hasRoomsChanged) {
      dispatch(clearBookingCalendar())
      slideTimer('fetchCalendarData', fetchCalendarData, 1000)
    }

    if (activeFilter) {
      autoScrollToFilters()
    }
  }, [activeFilter, activeHeaderFilter, previousFilter, hasRoomsChanged, fetchCalendarData]) // eslint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  const roomOption = useMemo(() => {
    if (query.roomTypeId && query.roomRateId) {
      return { roomTypeId: query.roomTypeId, roomRateId: query.roomRateId }
    }
  }, [query.roomTypeId, query.roomRateId])
  useEffect(() => {
    stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
  }, [stateDispatch, roomOption])

  useEffect(() => {
    slideTimer('fetchRelatedTravelItems', () => {
      dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
    }, 2000)
  }, [offer, duration, checkInDate, dispatch, rooms, checkOutDate])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  // Get the mapping for this property page so we know what bedbank offer to fetch
  const [mapping] = usePropertyOfferMapping(offer.property.id)
  // Get the bedbank offer for this property mapping
  const [bedbankOffer] = useOffer<App.BedbankOffer>(mapping?.bedbankPropertyId)

  // Ensure property rates (if applicable) are loaded.
  usePropertyRates({
    lpcPropertyId: offer.property.id,
    checkIn: checkInDate,
    checkOut: checkOutDate,
    occupancies: rooms,
    utmContent,
    roomTypeId: roomOption?.roomTypeId,
    roomRateId: roomOption?.roomRateId,
  })

  const occupancyLimits = useMemo(() => {
    const limits = offer.packages.flatMap((p) => p.roomRate.capacities)
    const lppLimits = bedbankOffer?.packages.flatMap((p) => p.capacities.combinations) ?? []
    return [
      ...limits,
      ...lppLimits,
    ]
  }, [offer.packages, bedbankOffer?.packages])

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  const bedbankOfferPath = useMemo(() => {
    if (!bedbankOffer?.promotions.length) return
    return `/${regionCode.toLowerCase()}${offerPageURL(bedbankOffer)}`
  }, [bedbankOffer, regionCode])
  if (bedbankOfferPath) {
    // Do _not_ perform a permanent redirect; we only need this temporary redirect fallback until we
    // notice the misconfiguration and disable the SPP. When the bedbank promotions expire, we'll
    // re-enable the SPP.
    return <RedirectWithStatus code={302} to={bedbankOfferPath} />
  }

  return <RelativeBlock onSubmit={(e) => e.preventDefault()}>
    {useNewMobileBookingFlow && <AccommodationOfferPageStickyHeaderMobile>
      <AlwaysOnMobileStickyHeaderFilters />
    </AccommodationOfferPageStickyHeaderMobile>}
    <div>
      <CSSBreakpoint min="tablet">
        <PropertyStickyHeader
          offer={offer}
          onViewPackageOptions={scrollToPackageOptions}
        />
      </CSSBreakpoint>
        <Breakpoint max="mobile">
          <ModalProvider>
            <AlwaysOnSidebarFilters
              onViewPackageOptions={scrollToPackageOptions}
              capacities={occupancyLimits}
              shouldShowCalendarWithoutPricing
            />
          </ModalProvider>
        </Breakpoint>
      <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
      <Pane>
        <PropertyIntro
          offer={offer}
          onViewPackageOptions={scrollToPackageOptions}
          shareConfig={shareConfig}
        />
      </Pane>
    </div>
    <ScrollAnchor ref={packageOptionsAnchor} />
    <PackageOptionsPane id="Package-Options">
      <StyledLayoutContainer className={cn({ 'no-padding': useNewMobileBookingFlow })}>
        <VerticalSpacer gap={32}>
          {availableForPurchase && (
            <>
              {!useNewMobileBookingFlow && (
                <CSSBreakpoint max="mobile">
                  <Group direction="vertical" gap={16}>
                    <Group direction="vertical" gap={8}>
                      {isLebtMultiRoomHotelEnabled && <BusinessTravellerAccountGuard
                        employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
                        loading={<BusinessTravellerSelectLoadingSkeleton />}
                        fallback={<BusinessTravellerMultiRoomHotelsOfferPageDesktopDisabled />}
                      >
                        <BusinessTravellerMultiRoomHotelsOfferPageDesktop />
                      </BusinessTravellerAccountGuard>}
                      {!isLebtMultiRoomHotelEnabled && <OfferCapacityWithAgesSelectBox />}
                      <OfferDatePickerSelectBox />
                    </Group>
                  </Group>
                </CSSBreakpoint>
              )}
              <CSSBreakpoint min="tablet">
                <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                  <DesktopFilterBlock ref={desktopFiltersWrapperRef} direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                    {isLebtMultiRoomHotelEnabled && <BusinessTravellerAccountGuard
                      employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
                      loading={<BusinessTravellerSelectLoadingSkeleton />}
                      fallback={<BusinessTravellerMultiRoomHotelsOfferPageDesktopDisabled />}
                    >
                      <BusinessTravellerMultiRoomHotelsOfferPageDesktop />
                    </BusinessTravellerAccountGuard>}
                    {!isLebtMultiRoomHotelEnabled && <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />}
                    <OfferDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                    <Backdrop visible={!!activeFilter && !isMobileDevice} data-testid="filter-backdrop" />
                  </DesktopFilterBlock>
              </CSSBreakpoint>
            </>
          )}
          {!useNewMobileBookingFlow && (
            <VerticalSpacer gap={8}>
              <Subtitle variant="subtitle3" align="center">
                Select duration
              </Subtitle>
              <OfferDurationTabs />
            </VerticalSpacer>
          )}
          {offer.packages.length > 0 && !useNewMobileBookingFlow && <PropertyRoomOptions />}
          {offer.packages.length > 0 && useNewMobileBookingFlow && <PropertyRoomOptionsMobile />}
        </VerticalSpacer>
      </StyledLayoutContainer>
    </PackageOptionsPane>
    {!useNewMobileBookingFlow && availableForPurchase &&
      <PropertyStickyFooter
        offer={offer}
        onViewPackageOptions={scrollToPackageOptions}
      />}
      {useNewMobileBookingFlow && availableForPurchase && <AccommodationOfferPageStickyFooter />}
  </RelativeBlock>
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'availableForPurchase'
  | 'room'
  | 'activeHeaderFilter'
  | 'regionCode'
  | 'utmContent'
  | 'selectedTravellerEmployeeIds'
  | 'selectedTravellerGuestCount'
  | 'currentBusinessAccount'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  room: state.room,
  availableForPurchase: isAvailableForPurchase(state, appState),
  activeHeaderFilter: state.activeHeaderFilter,
  regionCode: appState.geo.currentRegionCode,
  utmContent: appState.router.location.query.utm_content,
  selectedTravellerEmployeeIds: appState.businessTraveller.selectedTravellerEmployeeIds,
  selectedTravellerGuestCount: appState.businessTraveller.selectedTravellerGuestCount,
  currentBusinessAccount: appState.businessTraveller.currentBusinessAccount,
}), PropertyContent)
